export const META_PIXEL_ID = '2025365311589693'

type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void
  queue?: unknown[]
  push?: unknown
  loaded?: boolean
  version?: string
}

declare global {
  interface Window {
    fbq?: Fbq
    _fbq?: Fbq
  }
}

export type PixelLogEntry = {
  event: string
  at: string
  params?: Record<string, unknown>
}

const log: PixelLogEntry[] = []
const listeners = new Set<(entries: PixelLogEntry[]) => void>()

function record(event: string, params?: Record<string, unknown>) {
  log.unshift({
    event,
    at: new Date().toLocaleTimeString(),
    params,
  })
  listeners.forEach((listener) => listener([...log]))
  if (typeof console !== 'undefined') {
    console.info(`[Meta Pixel ${META_PIXEL_ID}] ${event}`, params ?? '')
  }
}

export function subscribeToPixelLog(listener: (entries: PixelLogEntry[]) => void) {
  listeners.add(listener)
  listener([...log])
  return () => {
    listeners.delete(listener)
  }
}

let initialized = false

/**
 * Carga el Meta Pixel y dispara PageView una sola vez por carga de página.
 * Se llama desde index.tsx para que ocurra lo antes posible.
 */
export function initMetaPixel() {
  if (typeof window === 'undefined' || initialized) return
  initialized = true

  if (!window.fbq) {
    const fbq: Fbq = function (...args: unknown[]) {
      if (fbq.callMethod) {
        fbq.callMethod.apply(fbq, args)
      } else {
        fbq.queue?.push(args)
      }
    } as Fbq

    fbq.queue = []
    fbq.push = fbq
    fbq.loaded = true
    fbq.version = '2.0'

    window.fbq = fbq
    window._fbq = fbq

    const script = document.createElement('script')
    script.async = true
    script.src = 'https://connect.facebook.net/en_US/fbevents.js'
    document.head.appendChild(script)
  }

  window.fbq?.('init', META_PIXEL_ID)
  window.fbq?.('track', 'PageView')
  sendPixelBeacon('PageView')
  record('PageView')
}

/**
 * Copia del evento enviada directamente al endpoint de Meta con una imagen 1x1.
 * Garantiza que el hit salga aunque fbevents.js todavía no haya terminado de
 * cargar o aunque la pestaña cambie de URL inmediatamente después del clic.
 */
function sendPixelBeacon(event: string, params?: Record<string, unknown>, eventId?: string) {
  const query = new URLSearchParams({
    id: META_PIXEL_ID,
    ev: event,
    dl: window.location.href,
    rl: document.referrer,
    if: 'false',
    ts: Date.now().toString(),
    v: '2.9.150',
    r: 'stable',
  })

  if (eventId) query.set('eid', eventId)

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null) return
      const serialized = typeof value === 'object' ? JSON.stringify(value) : String(value)
      query.set(`cd[${key}]`, serialized)
    })
  }

  const beacon = new Image(1, 1)
  beacon.referrerPolicy = 'no-referrer-when-downgrade'
  beacon.src = `https://www.facebook.com/tr?${query.toString()}`
}

/** Dispara un evento estándar del pixel (fbq + beacon de respaldo). */
export function trackPixelEvent(event: string, params?: Record<string, unknown>) {
  if (typeof window === 'undefined') return

  // Si algo impidió la inicialización (orden de carga), la reintentamos aquí.
  if (!window.fbq) initMetaPixel()

  const eventId = `${event}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`

  window.fbq?.('track', event, params, { eventID: eventId })
  sendPixelBeacon(event, params, eventId)
  record(event, params)
}
